import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import FastForwardOutlinedIcon from "@mui/icons-material/FastForwardOutlined";
import { TimelineItem as MuiTimelineItem, TimelineConnector, TimelineContent, TimelineDot, TimelineSeparator } from "@mui/lab";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DateTime } from "luxon";
import { INK, NEUTRAL } from "@/theme/tokens";
import { type AgentActionHistory, AgentActionType, AgentActionTypeDisplay } from "@/utils/models";

interface TimelineItemProps {
	action: AgentActionHistory | "posted";
	cumulativeNotified: number;
	hideSeperator?: boolean;
	dashedSeperator?: boolean;
	onEscalate?: () => void;
	isFuture?: boolean;
	getCallNurseName?: (action: AgentActionHistory) => string | undefined;
	isCalling?: boolean;
}

const getActionIcon = (action: AgentActionHistory | "posted", isFuture: boolean) => {
	if (!isFuture) return CheckCircleOutlineIcon;
	if (action === "posted") return CheckCircleOutlineIcon;
	return AgentActionTypeDisplay[action.action_type]?.icon ?? AgentActionTypeDisplay[AgentActionType.Regular].icon;
};

const getActionColor = (action: AgentActionHistory | "posted", primaryPurple: string) => {
	if (action === "posted") return primaryPurple;
	if (action.skip_reason) return NEUTRAL[500];
	return primaryPurple;
};

const getActionTitle = (
	action: AgentActionHistory | "posted",
	isFuture: boolean,
	getCallNurseName?: (action: AgentActionHistory) => string | undefined,
	isCalling?: boolean,
) => {
	if (action === "posted") return "Request Posted";
	if (action.skip_reason) return `Skipped: ${action.skip_reason}`;

	// Special handling for Call actions that are currently calling
	if (action.action_type === AgentActionType.Call && isCalling) {
		return "Calling";
	}

	const nurseName = action.action_type === AgentActionType.Call && getCallNurseName ? getCallNurseName(action) : undefined;
	const negativeResponded = action.negativeResponded;

	return (
		AgentActionTypeDisplay[action.action_type]?.notification(action.target_notification_count ?? 0, isFuture, nurseName, negativeResponded) ??
		AgentActionTypeDisplay[AgentActionType.Regular].notification(0, isFuture, undefined, negativeResponded)
	);
};

const getActionDetails = (action: AgentActionHistory | "posted", cumulativeNotified: number) => {
	if (action === "posted") return null;
	if (action.skip_reason) return null;
	const percentage = action.notification_percentage ? `${Math.round(action.notification_percentage * 100)}%` : "";
	return `${cumulativeNotified} of ${action.eligible_nurses_count} staff${percentage ? ` (${percentage})` : ""}`;
};

export const TimelineItem = ({
	action,
	hideSeperator,
	dashedSeperator,
	cumulativeNotified,
	onEscalate,
	isFuture,
	getCallNurseName,
	isCalling = false,
}: TimelineItemProps) => {
	const theme = useTheme();
	const IconComponent = getActionIcon(action, !!isFuture);
	const color = getActionColor(action, theme.palette.primary.main);

	return (
		<MuiTimelineItem
			key={action === "posted" ? "posted" : action.created_at}
			sx={{
				"&::before": {
					paddingLeft: 0,
					paddingRight: 0,
				},
				minHeight: 0,
			}}
		>
			<TimelineSeparator>
				{isFuture ? (
					<TimelineDot
						variant="outlined"
						sx={{ borderColor: color, borderWidth: 1, color, width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}
					>
						<IconComponent sx={{ fontSize: 12, color }} />
					</TimelineDot>
				) : (
					<TimelineDot variant="filled" sx={{ bgcolor: color, width: 20, height: 20 }} />
				)}
				<TimelineConnector
					sx={{
						...(hideSeperator ? { display: "none" } : {}),
						width: "1px",
						...(dashedSeperator
							? {
									borderLeft: `1px dashed ${NEUTRAL[400]}`,
									background: "none",
									ml: "2px",
								}
							: undefined),
					}}
				/>
			</TimelineSeparator>
			<TimelineContent>
				<Box
					sx={{
						p: "8px 10px",
						display: "flex",
						justifyContent: "space-between",
						border: `1px solid ${NEUTRAL[150]}`,
						height: "100%",
						borderRadius: "4px",
					}}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: !isFuture ? "row" : "column",
							alignItems: "space-between",
							justifyContent: "space-between",
							paddingRight: 0,
							height: "100%",
							width: "100%",
						}}
					>
						<Typography variant="body2" sx={{ fontWeight: 600, fontSize: "12px", color: theme.palette.primary.main, width: "fit-content" }}>
							{getActionTitle(action, !!isFuture, getCallNurseName, isCalling)}
						</Typography>
						{/* {action !== "posted" && action.action_type !== "evaluated" && getActionDetails(action, cumulativeNotified) && (
							<Typography variant="caption" sx={{ color: alpha(INK.SECONDARY, 0.5) }}>
								{getActionDetails(action, cumulativeNotified)}
							</Typography>
						)} */}
						{action === "posted" ? null : (
							<Typography variant="caption" sx={{ fontSize: "12px", color: alpha(INK.SECONDARY, 0.5) }}>
								{(() => {
									const actionDate = DateTime.fromISO(action.created_at);
									const now = DateTime.now();
									const secondsDiff = now.diff(actionDate, "second").as("seconds");
									return secondsDiff < 5 && secondsDiff > 0 ? "now" : actionDate.toRelative();
								})()}
							</Typography>
						)}
					</Box>
					{onEscalate && action !== "posted" && action.action_type !== "approach" && (
						<Box
							sx={{
								display: "flex",
								alignItems: "flex-end",
								height: "100%",
								justifyContent: "center",
							}}
						>
							<IconButton
								size="small"
								onClick={onEscalate}
								disabled={isCalling}
								sx={{
									border: "1px solid currentColor", // Adds a border
									borderRadius: "50%", // Keeps it circular
								}}
							>
								{isCalling ? <CircularProgress size={14} sx={{ color: theme.palette.primary.main }} /> : <FastForwardOutlinedIcon sx={{ fontSize: "14px" }} />}
							</IconButton>
						</Box>
					)}
				</Box>
			</TimelineContent>
		</MuiTimelineItem>
	);
};
